import { RefObject, useEffect, useRef, useState } from 'react';
import { ComboBoxItem } from '@/components/select/comboBoxTypes';
import { useQuery } from '@tanstack/react-query';

interface UseComboBoxQueryOptions<T extends ComboBoxItem> {
  queryKey: string[];
  queryFn?: (query?: string) => Promise<T[]>;
  initialQuery?: string;
  isInitializedRef: RefObject<boolean>;
  delay: number;
  closeOnBlur: boolean;
  openOnFocus: boolean;
  keepPreviousResults?: boolean;
}

const useComboBoxQuery = <T extends ComboBoxItem>({
  queryKey,
  queryFn,
  initialQuery = '',
  isInitializedRef,
  delay,
  closeOnBlur,
  openOnFocus,
  keepPreviousResults = false,
}: UseComboBoxQueryOptions<T>) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { isLoading, data } = useQuery({
    queryKey: [...queryKey, query],
    queryFn: () => queryFn?.(query),
    placeholderData: keepPreviousResults ? (previousData) => previousData : undefined,
  });

  useEffect(() => {
    if (query === '' && initialQuery !== '' && !isInitializedRef.current) {
      setQuery(initialQuery);
      return;
    }

    const current = inputRef.current;
    let timeoutId: null | ReturnType<typeof setTimeout> = null;

    const handleInputChange = () => {
      const nextInputValue = inputRef.current?.value;
      if (nextInputValue !== undefined) setInputValue(nextInputValue);
      if (
        isInitializedRef.current &&
        nextInputValue !== undefined &&
        ((query === '' && nextInputValue.length >= 3) || query !== '')
      ) {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => setQuery(nextInputValue), delay);
      }
    };

    current?.addEventListener('input', handleInputChange);

    return () => {
      current?.removeEventListener('input', handleInputChange);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [query, initialQuery, delay, isInitializedRef]);

  return {
    inputRef,
    open,
    query,
    inputValue,
    items: data ?? [],
    isLoading,
    handleFocus: () => {
      if (openOnFocus) setOpen(true);
    },
    handleBlur: () => {
      if (closeOnBlur) setOpen(false);
    },
    handleAfterLeave: () => {
      setQuery('');
      setInputValue('');
    },
    clearQuery: () => {
      setQuery('');
      setInputValue('');
      if (inputRef.current) inputRef.current.value = '';
    },
  };
};

export default useComboBoxQuery;
